import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { ArrowRight, Brain, Check, FlaskConical, Kanban, LogOut, Menu, Search, Shield, Sparkles, Users, X, Zap } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PESASimulatorSpot from './PESASimulatorSpot';
 

const PRICING_PARTICULIER = [
  {
    id: 'decouverte',
    name: 'Découverte',
    price: '0 €',
    period: '',
    description: 'Pour les particuliers et autodidactes',
    features: ["Centre d'Ajustement — cas de base", 'Parcours de découverte', 'Débrief automatique'],
    cta: 'Commencer gratuitement',
    accentColor: 'indigo',
  },
  {
    id: 'parcours',
    name: 'Parcours',
    price: '10 €',
    period: '/mois',
    trial: '30 jours',
    description: 'Pour approfondir à son rythme',
    features: ['Tous les cas et ateliers disponibles', 'Progression documentée', 'Certifications individuelles'],
    cta: "Démarrer l'essai",
    accentColor: 'amber',
  },
  {
    id: 'praticien',
    name: 'Praticien',
    price: '25 €',
    period: '/mois',
    trial: '30 jours',
    description: 'Pour intégrer PESACORE dans sa pratique',
    features: ['Tout Parcours inclus', 'Ateliers avancés — corpus étendu', 'Export des traces décisionnelles'],
    cta: "Démarrer l'essai",
    accentColor: 'orange',
  },
];

const PRICING_PLANS = [
  { id: 'academia', name: 'Academia', price: '2 400 €', period: '/an', trial: '30 jours', description: 'Pour les établissements éducatifs', features: ['Tous les parcours disponibles', 'Modèles de pensée systémique et causale', 'Simulations calibrées sur des cas réels', 'Analytics décisionnels', "Jusqu'à 150 apprenants", 'Support prioritaire'], cta: 'Démarrer essai 30j', highlighted: true, accentColor: 'amber' },
  { id: 'pro', name: 'Pro', price: '9 600 €', period: '/an', trial: '30 jours', description: 'Pour cabinets RH et consultants', features: ['Grande École (dashboard complet)', 'Gestion multi-cohortes', 'Simulations managériales', 'White-label branding', "Jusqu'à 500 collaborateurs", 'API v1 + intégrations'], cta: 'Démarrer essai 30j', highlighted: false, accentColor: 'orange' },
  { id: 'entreprise', name: 'Entreprise', price: 'Sur devis', period: '', trial: '30 jours', description: 'Solutions sur mesure', features: ['Utilisateurs illimités', 'Simulations custom (CaseCreator)', 'SSO + RGPD complet', 'Support dédié 24/7', 'SLA garanti', 'Consulting pédagogique'], cta: 'Nous contacter', highlighted: false, accentColor: 'amber' },
];

const ROLES = [
  {
    id: 'learner',
    name: 'Apprenant',
    icon: <Brain size={32} />,
    eyebrow: 'Particulier · Professionnel',
    tagline: 'Développez vos compétences par la pratique.',
    description: "Accédez à des situations réelles, progressez à votre rythme et documentez votre transformation. Chaque décision compte et laisse une trace exploitable.",
    features: [
      'Parcours adaptés à votre profil',
      'Mises en situation contextualisées',
      'Suivi de progression détaillé',
      'Portfolio de compétences exportable',
    ],
    accentColor: 'indigo',
  },
  {
    id: 'instructor',
    name: 'Enseignant · Formateur',
    icon: <Users size={32} />,
    eyebrow: 'Éducation · Formation professionnelle',
    tagline: 'Concevez des formations ancrées dans le réel.',
    description: "Créez des situations d'apprentissage complexes, suivez les processus décisionnels de vos apprenants et ajustez en temps réel.",
    features: [
      'Outil de création de situations guidé',
      'Suivi des parcours en temps réel',
      'Analyse des processus de décision',
      'Bibliothèque de situations partageables',
    ],
    accentColor: 'amber',
  },
  {
    id: 'admin',
    name: 'Administrateur',
    icon: <Shield size={32} />,
    eyebrow: 'Établissement · Organisation',
    tagline: 'Pilotez vos cohortes et mesurez les résultats.',
    description: "Gérez vos équipes, suivez les indicateurs de performance et produisez des rapports adaptés à vos obligations institutionnelles.",
    features: [
      'Tableau de bord multi-cohortes',
      'Gestion fine des accès et rôles',
      'Rapports personnalisables',
      'Intégrations LMS disponibles',
    ],
    accentColor: 'orange',
  },
]; 

const FAMILY_COLORS = {
  École: '#3b82f6', Travail: '#ef4444', Justice: '#6366f1',
  Art: '#f59e0b', Médiation: '#10b981', Famille: '#8b5cf6',
};

const DOMAINES = [{ label: "Éducation" }, { label: "Entreprise" }, { label: "Insertion" }, { label: "Institution" }];

const TABLEAU_ELEMENTS = [
  { 
    id: 'A', ref: 'SIT-2026-014', titre: 'Situation critique identifiée', type: 'DOSSIER', statut: 'OUVERT', 
    x: 4, y: 5,
    meta: { sousDossiers: 3, acteursImpliques: 4, derniereAct: '15/04/2026' }
  },
  { 
    id: 'B', ref: 'ACT-014-02', titre: 'Acteur clé isolé', type: 'ACTEUR', statut: null, 
    x: 54, y: 8,
    meta: { niveauAutonomie: 'faible', dernierEchange: '12/04/2026' }
  },
  { 
    id: 'C', ref: 'BIF-014-07', titre: 'Bifurcation non exploitée', type: 'TRACE', statut: null, 
    x: 24, y: 44,
    meta: { impactDirect: 12, dateDetection: '03/04/2026' }
  },
  { 
    id: 'D', ref: 'FRE-014-03', titre: 'Frein structurel', type: 'FREIN', statut: null, 
    x: 6, y: 70,
    meta: { gravite: 'élevée', solutionEnvisagee: 'en cours' }
  },
  { 
    id: 'E', ref: 'RES-014-01', titre: 'Impact mesuré', type: 'RÉSULTAT', statut: null, 
    x: 52, y: 68,
    meta: { progression: '+18%', delaiObtenu: '4 mois' }
  },
];

const FILS = [
  { de: 'A', a: 'B' },
  { de: 'A', a: 'C' },
  { de: 'B', a: 'C' },
  { de: 'C', a: 'D' },
  { de: 'C', a: 'E' },
];

const TableauClassique = () => {
  const [hoveindigo, setHoveindigo] = useState(null);
  const [revealed, setRevealed] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [cards, setCards] = useState(
    TABLEAU_ELEMENTS.map(el => ({
      ...el,
      statut: el.statut || '🔍 NON INSTRUIT',
      valeur: el.type === 'RÉSULTAT' ? '+18%' : (el.type === 'FREIN' ? 'bloquant' : 'à diagnostiquer'),
      params: { bienEtre: 50, equite: 50, mediation: 50 }
    }))
  );

  // Détection mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const TYPE_COLORS = {
    DOSSIER:  { border: 'rgba(34,211,238,0.35)',  bg: 'rgba(34,211,238,0.04)',  text: '#e06667',  pin: '#e06667' },
    TRACE:    { border: 'rgba(245,158,11,0.4)',   bg: 'rgba(245,158,11,0.05)',  text: '#f59e0b',  pin: '#f59e0b' },
    ACTEUR:   { border: 'rgba(139,92,246,0.4)',   bg: 'rgba(139,92,246,0.05)', text: '#a78bfa',  pin: '#a78bfa' },
    LEVIER:   { border: 'rgba(16,185,129,0.35)',  bg: 'rgba(16,185,129,0.04)', text: '#10b981',  pin: '#10b981' },
    FREIN:    { border: 'rgba(251,113,133,0.4)',  bg: 'rgba(251,113,133,0.05)', text: '#fb7185', pin: '#fb7185' },
    RÉSULTAT: { border: 'rgba(245,158,11,0.5)',   bg: 'rgba(245,158,11,0.07)', text: '#f59e0b',  pin: '#f59e0b' },
    OBJECTIF: { border: 'rgba(255,255,255,0.1)',  bg: 'rgba(255,255,255,0.02)', text: 'rgba(255,255,255,0.35)', pin: 'rgba(255,255,255,0.25)' },
  };

  const centre = (el) => ({ x: el.x + 14, y: el.y + 9 });

  useEffect(() => {
    const timers = TABLEAU_ELEMENTS.map((el, i) =>
      setTimeout(() => setRevealed(prev => [...prev, el.id]), 400 + i * 160)
    );
    return () => timers.forEach(clearTimeout);
  }, []);
  
  const updateCard = (id, action) => {
    setCards(prev => prev.map(card => {
      if (card.id !== id) return card;
      
      let newStatut = card.statut;
      let newValeur = card.valeur;
      let newParams = { ...card.params };
      
      switch (action) {
        case 'investiguer':
          newStatut = '📋 EN COURS';
          newValeur = 'causes identifiées';
          newParams.bienEtre = Math.max(0, newParams.bienEtre - 5);
          newParams.equite = Math.min(100, newParams.equite + 10);
          newParams.mediation = Math.min(100, newParams.mediation + 8);
          break;
        case 'classer':
          newStatut = '⏸ CLASSÉ';
          newValeur = 'sans suite';
          newParams.bienEtre = Math.max(0, newParams.bienEtre - 8);
          newParams.equite = Math.max(0, newParams.equite - 6);
          break;
        case 'mobiliser':
          newStatut = '🤝 EN RÉSEAU';
          newValeur = 'référent activé';
          newParams.mediation = Math.min(100, newParams.mediation + 15);
          newParams.bienEtre = Math.min(100, newParams.bienEtre + 8);
          break;
        case 'auditer':
          newStatut = '📊 AUDITÉ';
          newValeur = 'compétences cartographiées';
          newParams.equite = Math.min(100, newParams.equite + 12);
          break;
        case 'solution':
          newStatut = '🚌 DÉBLOQUÉ';
          newValeur = 'mobilité adaptée';
          newParams.bienEtre = Math.min(100, newParams.bienEtre + 12);
          newParams.mediation = Math.min(100, newParams.mediation + 6);
          break;
        default:
          break;
      }
      
      return { ...card, statut: newStatut, valeur: newValeur, params: newParams };
    }));
    setActiveCard(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeCard && !e.target.closest('.zoomed-card')) {
        setActiveCard(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeCard]);

  const actionsByType = {
    DOSSIER: [
      { label: '🔍 Investiguer', handlerKey: 'investiguer', impact: 'équité +10, médiation +8' },
      { label: '⏸ Classer', handlerKey: 'classer', impact: 'bien-être -6, équité -4' }
    ],
    ACTEUR: [
      { label: '🤝 Mobiliser', handlerKey: 'mobiliser', impact: 'médiation +15, bien-être +8' },
      { label: '📋 Auditer', handlerKey: 'auditer', impact: 'équité +12' }
    ],
    FREIN: [
      { label: '🚌 Solution', handlerKey: 'solution', impact: 'bien-être +12, médiation +6' }
    ],
    TRACE: [
      { label: '📈 Analyser', handlerKey: 'investiguer', impact: 'équité +9, médiation +7' }
    ],
    RÉSULTAT: [
      { label: '🏆 Valoriser', handlerKey: 'solution', impact: 'bien-être +10, équité +5' }
    ]
  };

  const getCardFront = (el, c, hov) => {
    const isZoomed = activeCard === el.id;
    const currentCard = cards.find(card => card.id === el.id);
    const zoomScale = 1.4;
    
    // Sur mobile : même UX que desktop, tailles réduites
    if (isMobile) {
      return (
        <motion.div
          key={`card-${el.id}`}
          initial={{ opacity: 0, scale: 0.8, y: 8 }}
          animate={{ 
            opacity: revealed.includes(el.id) ? 1 : 0, 
            scale: isZoomed ? zoomScale : 1, 
            y: isZoomed ? -10 : 0,
            x: isZoomed ? (el.x > 45 ? '-45%' : '5%') : 0,
            zIndex: isZoomed ? 100 : (hov ? 30 : 10)
          }}
          whileHover={!isZoomed ? { 
            scale: 1.6, 
            y: -8,
            transition: { duration: 0.2, ease: [0.2, 1, 0.3, 1] }
          } : {}}
          transition={{ duration: 0.2, ease: [0.2, 1, 0.3, 1] }}
          onMouseEnter={() => !isZoomed && setHoveindigo(el.id)}
          onMouseLeave={() => !isZoomed && setHoveindigo(null)}
          onClick={(e) => { 
            e.stopPropagation(); 
            if (!isZoomed) setActiveCard(el.id);
          }}
          style={{ 
            position: 'absolute', 
            left: `${el.x}%`, 
            top: `${el.y}%`, 
            width: isZoomed ? 'auto' : '30%',
            minWidth: isZoomed ? '220px' : 'auto',  // ← réduit pour mobile
            maxWidth: isZoomed ? '280px' : 'auto',   // ← limite la largeur max
            zIndex: isZoomed ? 100 : (hov ? 30 : 10), 
            cursor: 'pointer'
          }}
          className={isZoomed ? 'zoomed-card' : ''}
        >
          <div style={{ 
            position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', 
            width: isZoomed ? 8 : 6, height: isZoomed ? 8 : 6, borderRadius: '50%', 
            background: c.pin, boxShadow: (hov || isZoomed) ? `0 0 10px 3px ${c.pin}` : 'none', 
            transition: 'box-shadow 0.25s', zIndex: 2 
          }} />
          <div style={{ 
            background: (hov || isZoomed) ? 'rgba(2,6,23,0.97)' : 'rgba(2,6,23,0.85)', 
            border: `1px solid ${(hov || isZoomed) ? c.border.replace(/[\d.]+\)$/, '0.65)') : c.border}`, 
            backdropFilter: 'blur(12px)', 
            padding: isZoomed ? '0.7rem 0.9rem' : '0.35rem 0.5rem',
            boxShadow: (hov || isZoomed) ? '0 8px 28px rgba(0,0,0,0.4)' : 'none', 
            transition: 'all 0.2s',
            borderRadius: isZoomed ? '10px' : '6px'
          }}>
            {/* En-tête */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isZoomed ? '0.4rem' : '0.15rem' }}>
              <span style={{ 
                fontFamily: 'monospace', 
                fontSize: isZoomed ? '0.6rem' : '0.3rem', 
                letterSpacing: '0.12em', 
                textTransform: 'uppercase', 
                color: c.text, 
                opacity: 0.8 
              }}>{el.ref}</span>
              <span style={{ 
                fontFamily: 'monospace', 
                fontSize: isZoomed ? '0.5rem' : '0.24rem', 
                padding: isZoomed ? '0.1rem 0.4rem' : '0.04rem 0.2rem', 
                border: `1px solid ${c.border}`, 
                color: c.text,
                borderRadius: '2px'
              }}>{currentCard?.statut}</span>
            </div>
            
            {/* Titre */}
            <p style={{ 
              fontFamily: "'Georgia', serif", 
              fontSize: isZoomed ? '0.85rem' : '0.45rem', 
              color: '#e2e8f0', 
              lineHeight: 1.3, 
              marginBottom: isZoomed ? '0.4rem' : '0.12rem',
              fontWeight: isZoomed ? 500 : 400
            }}>{el.titre}</p>
            
            {/* Type et valeur */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ 
                fontFamily: 'monospace', 
                fontSize: isZoomed ? '0.5rem' : '0.26rem', 
                letterSpacing: '0.12em', 
                textTransform: 'uppercase', 
                color: 'rgba(255,255,255,0.3)' 
              }}>{el.type}</span>
              {el.type === 'RÉSULTAT' && (
                <span style={{ fontFamily: 'monospace', fontSize: isZoomed ? '0.5rem' : '0.26rem', color: '#10b981' }}>{currentCard?.valeur}</span>
              )}
              {el.type === 'FREIN' && (
                <span style={{ fontFamily: 'monospace', fontSize: isZoomed ? '0.5rem' : '0.26rem', color: '#fb7185' }}>{currentCard?.valeur}</span>
              )}
            </div>
            
            {/* Actions et paramètres (uniquement quand zoomé) */}
            {isZoomed && (
              <div style={{ marginTop: '0.7rem', paddingTop: '0.5rem', borderTop: `1px solid ${c.border}40` }}>
                <div style={{ marginBottom: '0.4rem' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.45rem', color: c.text, letterSpacing: '0.08em' }}>ACTIONS</span>
                </div>
                {(actionsByType[el.type] || actionsByType.DOSSIER).map((act, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); updateCard(el.id, act.handlerKey); }}
                    style={{
                      width: '100%', textAlign: 'left', marginBottom: '0.3rem', padding: '0.3rem 0.5rem',
                      background: 'rgba(255,255,255,0.03)', border: `1px solid ${c.border}25`, borderRadius: '6px',
                      fontSize: '0.55rem', color: '#fff', cursor: 'pointer', fontFamily: 'monospace',
                    }}
                  >
                    <span>{act.label}</span>
                    <span style={{ display: 'block', fontSize: '0.4rem', color: '#f59e0b', marginTop: '2px' }}>{act.impact}</span>
                  </button>
                ))}
                
                {/* Paramètres */}
                <div style={{ marginTop: '0.4rem', paddingTop: '0.3rem', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', fontSize: '0.4rem', fontFamily: 'monospace', color: '#94a3b8' }}>
                    <span>B: {currentCard?.params.bienEtre}</span>
                    <span>É: {currentCard?.params.equite}</span>
                    <span>M: {currentCard?.params.mediation}</span>
                  </div>
                </div>
                
                {/* Fermeture */}
                <button 
                  onClick={(e) => { e.stopPropagation(); setActiveCard(null); }}
                  style={{ marginTop: '0.5rem', width: '100%', padding: '0.25rem', background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: '4px', fontSize: '0.45rem', color: '#64748b', cursor: 'pointer', fontFamily: 'monospace' }}
                >
                  ✕ Fermer
                </button>
              </div>
            )}
          </div>
        </motion.div>
      );
    }
    
    // ----- DESKTOP (inchangé, exactement comme avant) -----
    return (
      <motion.div
        key={`card-${el.id}`}
        initial={{ opacity: 0, scale: 0.8, y: 8 }}
        animate={{ 
          opacity: revealed.includes(el.id) ? 1 : 0, 
          scale: isZoomed ? zoomScale : 1, 
          y: isZoomed ? -10 : 0,
          x: isZoomed ? '5%' : 0,
          zIndex: isZoomed ? 100 : (hov ? 30 : 10)
        }}
        whileHover={!isZoomed ? { 
          scale: 1.6, 
          y: -8,
          transition: { duration: 0.2, ease: [0.2, 1, 0.3, 1] }
        } : {}}
        transition={{ duration: 0.2, ease: [0.2, 1, 0.3, 1] }}
        onMouseEnter={() => !isZoomed && setHoveindigo(el.id)}
        onMouseLeave={() => !isZoomed && setHoveindigo(null)}
        onClick={(e) => { 
          e.stopPropagation(); 
          if (!isZoomed) setActiveCard(el.id);
        }}
        style={{ 
          position: 'absolute', 
          left: `${el.x}%`, 
          top: `${el.y}%`, 
          width: isZoomed ? 'auto' : '30%',
          minWidth: isZoomed ? '260px' : 'auto',
          zIndex: isZoomed ? 100 : (hov ? 30 : 10), 
          cursor: 'pointer'
        }}
        className={isZoomed ? 'zoomed-card' : ''}
      >
        {/* Desktop content — strictement identique à l'original */}
        <div style={{ 
          position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', 
          width: isZoomed ? 10 : 7, height: isZoomed ? 10 : 7, borderRadius: '50%', 
          background: c.pin, boxShadow: (hov || isZoomed) ? `0 0 14px 4px ${c.pin}` : 'none', 
          transition: 'box-shadow 0.25s', zIndex: 2 
        }} />
        <div style={{ 
          background: (hov || isZoomed) ? 'rgba(2,6,23,0.97)' : 'rgba(2,6,23,0.85)', 
          border: `1px solid ${(hov || isZoomed) ? c.border.replace(/[\d.]+\)$/, '0.65)') : c.border}`, 
          backdropFilter: 'blur(12px)', 
          padding: isZoomed ? '1rem 1.2rem' : '0.5rem 0.65rem',
          boxShadow: (hov || isZoomed) ? '0 8px 28px rgba(0,0,0,0.4)' : 'none', 
          transition: 'all 0.2s',
          borderRadius: isZoomed ? '12px' : '0'
        }}>
          {/* En-tête */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isZoomed ? '0.6rem' : '0.22rem' }}>
            <span style={{ 
              fontFamily: 'monospace', 
              fontSize: isZoomed ? '0.75rem' : '0.36rem', 
              letterSpacing: '0.15em', 
              textTransform: 'uppercase', 
              color: c.text, 
              opacity: 0.8 
            }}>{el.ref}</span>
            <span style={{ 
              fontFamily: 'monospace', 
              fontSize: isZoomed ? '0.6rem' : '0.28rem', 
              padding: isZoomed ? '0.15rem 0.5rem' : '0.08rem 0.3rem', 
              border: `1px solid ${c.border}`, 
              color: c.text,
              borderRadius: '2px'
            }}>{currentCard?.statut}</span>
          </div>
          
          {/* Titre */}
          <p style={{ 
            fontFamily: "'Georgia', serif", 
            fontSize: isZoomed ? '1.1rem' : '0.56rem', 
            color: '#e2e8f0', 
            lineHeight: 1.3, 
            marginBottom: isZoomed ? '0.6rem' : '0.18rem',
            fontWeight: isZoomed ? 500 : 400
          }}>{el.titre}</p>
          
          {/* Type et valeur */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ 
              fontFamily: 'monospace', 
              fontSize: isZoomed ? '0.6rem' : '0.3rem', 
              letterSpacing: '0.15em', 
              textTransform: 'uppercase', 
              color: 'rgba(255,255,255,0.3)' 
            }}>{el.type}</span>
            {el.type === 'RÉSULTAT' && (
              <span style={{ fontFamily: 'monospace', fontSize: isZoomed ? '0.6rem' : '0.32rem', color: '#10b981' }}>{currentCard?.valeur}</span>
            )}
            {el.type === 'FREIN' && (
              <span style={{ fontFamily: 'monospace', fontSize: isZoomed ? '0.6rem' : '0.32rem', color: '#fb7185' }}>{currentCard?.valeur}</span>
            )}
          </div>
          
          {/* Actions et paramètres (uniquement quand zoomé) */}
          {isZoomed && (
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${c.border}40` }}>
              <div style={{ marginBottom: '0.6rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: c.text, letterSpacing: '0.1em' }}>ACTIONS</span>
              </div>
              {(actionsByType[el.type] || actionsByType.DOSSIER).map((act, i) => (
              
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); updateCard(el.id, act.handlerKey); }}
                  style={{
                    width: '100%', textAlign: 'left', marginBottom: '0.4rem', padding: '0.4rem 0.6rem',
                    background: 'rgba(255,255,255,0.03)', border: `1px solid ${c.border}25`, borderRadius: '6px',
                    fontSize: '0.6rem', color: '#fff', cursor: 'pointer', fontFamily: 'monospace',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                >
                  <span>{act.label}</span>
                  <span style={{ display: 'block', fontSize: '0.45rem', color: '#f59e0b', marginTop: '2px' }}>{act.impact}</span>
                </button>
              ))}
              
              {/* Paramètres */}
              <div style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', fontSize: '0.45rem', fontFamily: 'monospace', color: '#94a3b8' }}>
                  <span>Bien-être: {currentCard?.params.bienEtre}</span>
                  <span>Équité: {currentCard?.params.equite}</span>
                  <span>Médiation: {currentCard?.params.mediation}</span>
                </div>
              </div>

              {el.meta && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.3rem', borderTop: `1px solid ${c.border}30`, fontSize: '0.45rem', fontFamily: 'monospace', color: '#64748b' }}>
                  {Object.entries(el.meta).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                      <span>{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                      <span style={{ color: '#f59e0b' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Fermeture */}
              <button 
                onClick={(e) => { e.stopPropagation(); setActiveCard(null); }}
                style={{ marginTop: '0.6rem', width: '100%', padding: '0.3rem', background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: '4px', fontSize: '0.5rem', color: '#64748b', cursor: 'pointer', fontFamily: 'monospace' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              >
                ✕ Fermer
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  function StandalonePESAField({ dimmed = false }) {
    const containerRef = useRef(null);
    const canvasRef    = useRef(null);
  
    useEffect(() => {
      const container = containerRef.current;
      const canvas    = canvasRef.current;
      if (!canvas || !container) return;
      const ctx = canvas.getContext('2d');
      let id; let time = 0;
  
      const FAMILIES = Object.entries(FAMILY_COLORS).map(([name, color], i, arr) => {
        const angle  = (i / arr.length) * Math.PI * 2 - Math.PI / 2;
        const stress = Math.random() * 0.6 + 0.2;
        return { name, color, angle, stress };
      });
  
      const AGENTS = FAMILIES.flatMap(f =>
        Array.from({ length: Math.floor(Math.random() * 3) + 1 }, (_, j) => ({
          name: `${f.name[0]}${j + 1}`, family: f.name, color: f.color,
        }))
      );
  
      const resize = () => {
        const dpr = window.devicePixelRatio || 1;
        canvas.width  = container.clientWidth  * dpr;
        canvas.height = container.clientHeight * dpr;
        ctx.scale(dpr, dpr);
      };
      resize();
      window.addEventListener('resize', resize);
  
      const draw = () => {
        time += 0.008;
        const w = container.clientWidth, h = container.clientHeight;
        const cx = w / 2, cy = h / 2;
        ctx.clearRect(0, 0, w, h);
  
        const nodes = FAMILIES.map(f => ({
          ...f,
          x: cx + Math.cos(f.angle) * Math.min(w, h) * 0.33,
          y: cy + Math.sin(f.angle) * Math.min(w, h) * 0.33,
        }));
  
        nodes.forEach((a, i) => nodes.slice(i + 1).forEach(b => {
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.quadraticCurveTo(
            cx + Math.sin(time * 0.5) * 8,
            cy + Math.cos(time * 0.5) * 8,
            b.x, b.y
          );
          ctx.strokeStyle = 'rgba(255,255,255,0.03)';
          ctx.lineWidth = 1; ctx.stroke();
        }));
  
        [[0, 2], [1, 4]].forEach(([ai, bi]) => {
          if (!nodes[ai] || !nodes[bi]) return;
          const a = nodes[ai], b = nodes[bi];
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = 'rgba(16,185,129,0.12)'; ctx.lineWidth = 2; ctx.stroke();
          const t2 = (time * 0.015) % 1;
          const px = a.x + (b.x - a.x) * t2, py = a.y + (b.y - a.y) * t2;
          ctx.beginPath(); ctx.arc(px, py, 2, 0, Math.PI * 2);
          ctx.fillStyle = '#10b981'; ctx.shadowBlur = 6; ctx.shadowColor = '#10b981'; ctx.fill(); ctx.shadowBlur = 0;
        });
  
        nodes.forEach(n => {
          const pulse = Math.sin(time * 2.5 + n.x * 0.01) * 1.5;
          const size  = 20 + n.stress * 14 + pulse;
  
          if (n.stress > 0.4) {
            const gr = ctx.createRadialGradient(n.x, n.y, size, n.x, n.y, size * 2.2);
            gr.addColorStop(0, n.color + '30'); gr.addColorStop(1, 'transparent');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(n.x, n.y, size * 2.2, 0, Math.PI * 2); ctx.fill();
          }
  
          const ng = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, size);
          ng.addColorStop(0, n.color); ng.addColorStop(1, n.color + 'CC');
          ctx.beginPath(); ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
          ctx.fillStyle = ng; ctx.fill();
          ctx.strokeStyle = 'rgba(255,255,255,0.75)'; ctx.lineWidth = 1.5; ctx.stroke();
  
          ctx.font = 'bold 10px monospace'; ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
          ctx.shadowBlur = 3; ctx.shadowColor = 'rgba(0,0,0,0.9)';
          ctx.fillText(n.name.toUpperCase(), n.x, n.y - size - 8);
          ctx.shadowBlur = 0;
  
          AGENTS.filter(a => a.family === n.name).forEach((a, idx) => {
            const oa = time + idx * (Math.PI * 2 / 3);
            const ox = n.x + Math.cos(oa) * (size + 10);
            const oy = n.y + Math.sin(oa) * (size + 10);
            ctx.beginPath(); ctx.arc(ox, oy, 2, 0, Math.PI * 2);
            ctx.fillStyle = n.color; ctx.shadowBlur = 4; ctx.shadowColor = n.color; ctx.fill(); ctx.shadowBlur = 0;
          });
        });
  
        ctx.font = '9px monospace'; ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.textAlign = 'left';
        ctx.fillText('Entropy: 0.62', 12, h - 24);
        ctx.fillText(`Links: ${AGENTS.length}`, 12, h - 12);
  
        id = requestAnimationFrame(draw);
      };
      draw();
      return () => { cancelAnimationFrame(id); window.removeEventListener('resize', resize); };
    }, []);
  
    return (
      <div ref={containerRef} className={`w-full h-full relative ${dimmed ? 'opacity-30' : 'opacity-100'}`}>
        <canvas ref={canvasRef} className="w-full h-full" />
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      //aspectRatio: '4/3',
      minHeight: 400, 
      maxWidth: 520, 
      background: 'rgba(2,6,23,0.65)', 
      backdropFilter: 'blur(16px)', 
      border: '1px solid rgba(255,255,255,0.06)', 
      overflow: 'visible',  // ← modifié pour que les cartes ne soient pas coupées
      
    }}>
      
      {/* Canvas PESA en arrière-plan */}
      <div className="absolute inset-0 z-0 rounded-lg overflow-hidden opacity-50">
        <StandalonePESAField />
      </div>

      {/* Coins HUD */}
      {[['top:-1px;left:-1px', 'borderTop:2px solid #e06667;borderLeft:2px solid #e06667'], ['top:-1px;right:-1px', 'borderTop:2px solid rgba(34,211,238,0.25);borderRight:2px solid rgba(34,211,238,0.25)'], ['bottom:-1px;left:-1px', 'borderBottom:2px solid rgba(245,158,11,0.25);borderLeft:2px solid rgba(245,158,11,0.25)'], ['bottom:-1px;right:-1px', 'borderBottom:2px solid #f59e0b;borderRight:2px solid #f59e0b']].map(([pos, bdr], i) => (
        <span key={i} style={{ position: 'absolute', ...Object.fromEntries(pos.split(';').map(s => { const [k, v] = s.split(':'); return [k, v]; })), width: 14, height: 14, ...Object.fromEntries(bdr.split(';').map(s => { const [k, v] = s.split(':'); return [k.trim(), v.trim()]; })), zIndex: 20 }} />
      ))}

      {/* Accent top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.5), rgba(245,158,11,0.5), transparent)', zIndex: 20 }} />

      {/* Grille liège */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(34,211,238,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.035) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {/* Grain */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.025, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat' }} />

      {/* SVG fils */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 5, pointerEvents: 'none' }}>
        {FILS.map((fil, i) => {
          const from = TABLEAU_ELEMENTS.find(e => e.id === fil.de);
          const to = TABLEAU_ELEMENTS.find(e => e.id === fil.a);
          if (!from || !to) return null;
          const cf = centre(from), ct = centre(to);
          const active = hoveindigo === fil.de || hoveindigo === fil.a;
          return (
            <motion.line key={i}
              x1={`${cf.x}%`} y1={`${cf.y}%`} x2={`${ct.x}%`} y2={`${ct.y}%`}
              stroke={active ? '#f59e0b' : 'rgba(245,158,11,0.18)'}
              strokeWidth={active ? 1.5 : 0.8}
              strokeDasharray={active ? 'none' : '5 4'}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.9, delay: 1 + i * 0.12 }}
              style={{ transition: 'stroke 0.2s, stroke-width 0.2s' }}
            />
          );
        })}
        {FILS.map((fil, i) => {
          const from = TABLEAU_ELEMENTS.find(e => e.id === fil.de);
          const cf = centre(from);
          return (
            <motion.circle key={`pt-${i}`} cx={`${cf.x}%`} cy={`${cf.y}%`} r="2.5"
              fill="rgba(245,158,11,0.35)"
              initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.4 + i * 0.1, type: 'spring', stiffness: 200 }}
            />
          );
        })}
      </svg>

      {/* Cartes */}
      {TABLEAU_ELEMENTS.map((el) => {
        const c = TYPE_COLORS[el.type] || TYPE_COLORS.OBJECTIF;
        const vis = revealed.includes(el.id);
        const hov = hoveindigo === el.id;
        if (!vis) return null;
        return getCardFront(el, c, hov);
      })}

      {/* Barre de statut bas */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0.45rem 0.85rem', background: 'rgba(2,6,23,0.9)', borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 25 }}>
        <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(34,211,238,0.45)' }}>Tableau d'investigation</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', animation: 'dotpulse 2s ease-in-out infinite' }} />
          <span style={{ fontFamily: 'monospace', fontSize: '0.36rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(16,185,129,0.55)' }}>
            {isMobile ? `${cards.filter(c => c.statut !== '🔍 NON INSTRUIT').length}/${cards.length} instruits` : 'cliquez sur une carte — instruisez le dossier'}
          </span>
        </div>
      </div>

      {/* Scan line */}
      <motion.div animate={{ top: ['-2%', '102%'] }} transition={{ duration: 7, repeat: Infinity, ease: 'linear', repeatDelay: 4 }}
        style={{ position: 'absolute', left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.2), rgba(245,158,11,0.25), transparent)', zIndex: 20, pointerEvents: 'none' }}
      />
    </div>
  );
};

const TableauEnquete = () => {
  const [mode, setMode] = useState('classique');

  return (
    <div className="relative">
      {/* En-tête avec bouton de basculement */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-[9px] font-mono tracking-[0.3em] text-amber-400/60 uppercase">
            {mode === 'classique' ? 'TABLEAU D\'INVESTIGATION' : 'LABORATOIRE DE SIMULATION'}
          </span>
        </div>
        
        <button
          onClick={() => setMode(mode === 'classique' ? 'laboratoire' : 'classique')}
          className="group flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:border-amber-500/30 transition-all duration-300"
        >
          {mode === 'classique' ? (
            <>
              <FlaskConical size={12} className="text-purple-400 group-hover:text-purple-300 transition-colors" />
              <span className="text-[9px] font-mono uppercase tracking-wider text-white/50 group-hover:text-white/80">
                Mode laboratoire
              </span>
            </>
          ) : (
            <>
              <Kanban size={12} className="text-amber-400 group-hover:text-amber-300 transition-colors" />
              <span className="text-[9px] font-mono uppercase tracking-wider text-white/50 group-hover:text-white/80">
                Retour à l'enquête
              </span>
            </>
          )}
        </button>
      </div>

      {/* Conteneur avec transition — PLUS DE overflow-hidden */}
      <div className="relative rounded-xl border border-white/5 bg-black/20 backdrop-blur-sm">
        <AnimatePresence mode="wait">
          {mode === 'classique' && (
            <motion.div
              key="classique"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              <TableauClassique />
            </motion.div>
          )}
          
          {mode === 'laboratoire' && (
            <motion.div
              key="laboratoire"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{ width: '100%', height: '480px', overflowX: 'hidden', borderRadius: '12px' }}
            >
              <PESASimulatorSpot initialStep={1} autoPlay={true} showTimeline={true} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Légende contextuelle */}
      <div className="mt-3 text-center">
        {mode === 'classique' ? (
          <p className="text-[9px] font-mono text-white/20 uppercase tracking-wider">
            {TABLEAU_ELEMENTS.length} éléments · {FILS.length} connexions · cliquez sur une carte
          </p>
        ) : (
          <p className="text-[9px] font-mono text-white/20 uppercase tracking-wider">
            simulation causale · paramètres dynamiques · prédiction en temps réel
          </p>
        )}
      </div>
    </div>
  );
};

export const FloatingWords = () => {
  const words = ["Investigation", "Bifurcation", "Bourdieu", "Freire", "Simulation", "Causalité", "Systémique", "Praxis", "Conscience", "Émancipation", "Habitus", "Reconstitution", "Dossier", "Subversion", "Transformation"];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.06]">
      {words.map((word, i) => {
        const depth = i % 3;
        const styles = [{ size: 'text-xs', opacity: 0.15, speed: 45 }, { size: 'text-lg', opacity: 0.3, speed: 35 }, { size: 'text-2xl', opacity: 0.5, speed: 25 }][depth];
        const color = i % 2 === 0 ? 'text-indigo-100' : 'text-amber-100';
        return (
          <motion.div key={word} initial={{ opacity: 0, y: '110vh', x: `${20 + (i * 7.3 % 60)}vw` }} animate={{ opacity: [0, styles.opacity, styles.opacity, 0], y: '-20vh' }} transition={{ duration: styles.speed, repeat: Infinity, delay: i * 2.5, ease: 'linear' }}
            className={`absolute whitespace-nowrap font-extralight tracking-[0.3em] uppercase select-none ${styles.size} ${color}`}
          >{word}</motion.div>
        );
      })}
    </div>
  );
};

export const PerspectiveGrid = () => (
  <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(to right, rgba(34,211,238,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(245,158,11,0.1) 1px, transparent 1px)`, backgroundSize: '120px 120px', maskImage: 'radial-gradient(circle at 50% 50%, black 65%, transparent 100%)', WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 65%, transparent 100%)' }} />
);

export const LiveClock = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);
  return <span>{time}</span>;
};

const NavLink = ({ to, children, accent = '#f59e0b', index }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);
  const [hoveindigo, setHoveindigo] = useState(false);
  return (
    <Link to={to} onMouseEnter={() => setHoveindigo(true)} onMouseLeave={() => setHoveindigo(false)} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '2px', textDecoration: 'none', padding: '0.5rem 0' }}>
      <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.3em', color: hoveindigo || isActive ? accent : 'rgba(255,255,255,0.15)', transition: 'color 0.25s', lineHeight: 1 }}>{String(index).padStart(2, '0')} ·</span>
      <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: hoveindigo || isActive ? '#fff' : 'rgba(255,255,255,0.45)', transition: 'color 0.25s', lineHeight: 1 }}>{children}</span>
      <motion.span animate={{ scaleX: hoveindigo || isActive ? 1 : 0 }} initial={{ scaleX: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: `linear-gradient(to right, ${accent}, transparent)`, transformOrigin: 'left' }} />
      {isActive && <span style={{ position: 'absolute', top: '50%', left: '-12px', transform: 'translateY(-50%)', width: '4px', height: '4px', borderRadius: '50%', background: accent, boxShadow: `0 0 8px ${accent}` }} />}
    </Link>
  );
};

const NAV_ITEMS = [
  { to: '/home-page',  label: 'Accueil',    accent: '#45dfd2', i: 0 },  
  { to: '/academia',   label: 'Academia',   accent: '#f59e0b', i: 1 },
  { to: '/entreprise', label: 'Entreprise', accent: '#f97316', i: 2 },
  { to: '/partners',   label: 'Partenaire', accent: '#e06667', i: 3 },
  { to: '/manifeste',  label: 'Manifeste',  accent: '#94a3b8', i: 4 },
  { to: '/contact',    label: 'Contact',    accent: '#184b92', i: 5 },
];

export const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (y) => setScrolled(y > 40));
  useEffect(() => { const r = () => { if (window.innerWidth >= 768) setIsMenuOpen(false); }; window.addEventListener('resize', r); return () => window.removeEventListener('resize', r); }, []);
  useEffect(() => { document.body.style.overflow = isMenuOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [isMenuOpen]);

  return (
    <>
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes dotpulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @media (max-width:767px) { .hud-top-rail,.nav-desktop,.auth-desktop{display:none!important} .burger-btn{display:flex!important} }
        @media (min-width:768px) { .burger-btn{display:none!important} .nav-desktop,.auth-desktop{display:flex!important} }
      `}</style>

      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
        

        <div style={{ background: scrolled ? 'rgba(2,6,23,0.97)' : 'rgba(2,6,23,0.85)', backdropFilter: 'blur(24px)', borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.84)' : '1px solid rgba(255,255,255,0.04)', transition: 'background 0.4s, border-color 0.4s' }}>
          <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 1.25rem', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to="/landing-pesacore" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
              <div style={{ position: 'relative', width: '32px', height: '32px', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.04)' }}>
                <span style={{ position: 'absolute', top: '-1px', left: '-1px', width: '7px', height: '7px', borderTop: '2px solid #e06667', borderLeft: '2px solid #e06667' }} />
                <span style={{ position: 'absolute', bottom: '-1px', right: '-1px', width: '7px', height: '7px', borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />
                <img src="/logo2026.png" alt="P" style={{ width: '20px', height: '20px', objectFit: 'contain', filter: 'brightness(1.2)' }} />
              </div>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.06em', color: '#fff', lineHeight: 1.1 }}>PESA<span style={{ background: 'linear-gradient(90deg,#e06667,#f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CORE</span></div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.55rem', letterSpacing: '0.32em', color: 'rgba(255, 255, 255, 0.81)', textTransform: 'uppercase', lineHeight: 1 }}>Architect</div>
              </div>
            </Link>

            <nav className="nav-desktop" style={{ alignItems: 'center', gap: '2.5rem', paddingLeft: '2.5rem', paddingRight: '2.5rem', borderLeft: '1px solid rgba(255,255,255,0.04)', borderRight: '1px solid rgba(255,255,255,0.04)' }}>
              {NAV_ITEMS.map(({ to, label, accent, i }) => <NavLink key={to} to={to} accent={accent} index={i}>{label}</NavLink>)}
            </nav>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              <div className="auth-desktop" style={{ alignItems: 'center', gap: '0.5rem' }}>
                {!isAuthenticated ? (
                  <>
                    <Link to="/auth/login" style={{ fontFamily: 'monospace', fontSize: '0.6rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', padding: '0.5rem 0.75rem', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}>Connexion</Link>
                    <Link to="/auth/register" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.2rem', fontFamily: 'monospace', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', textDecoration: 'none', color: '#020617', background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)', transition: 'box-shadow 0.3s' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 24px rgba(245,158,11,0.5)'} onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <span style={{ display: 'inline-block', width: '5px', height: '5px', borderRadius: '50%', background: '#020617', opacity: 0.5, animation: 'blink 1.4s ease-in-out infinite' }} />
                      Inscription
                    </Link>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                    <button onClick={() => navigate('/users/me')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', background: 'none', border: 'none', cursor: 'pointer', color: '#e06667' }}><Users size={15} /></button>
                    <span style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.06)' }} />
                    <button onClick={logout} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(239,68,68,0.6)' }}><LogOut size={15} /></button>
                  </div>
                )}
              </div>
              <button className="burger-btn" onClick={() => setIsMenuOpen(o => !o)} style={{ alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: isMenuOpen ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isMenuOpen ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.08)'}`, color: isMenuOpen ? '#f59e0b' : 'rgba(255,255,255,0.6)', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}>
                {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <>
              <motion.div key="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMenuOpen(false)} style={{ position: 'fixed', inset: 0, top: '58px', background: 'rgba(2,6,23,0.7)', backdropFilter: 'blur(4px)', zIndex: -1 }} />
              <motion.div key="drawer" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.22 }} style={{ background: 'rgba(2,6,23,0.98)', backdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(245,158,11,0.12)' }}>
                <nav style={{ padding: '0.5rem 0' }}>
                  {NAV_ITEMS.map(({ to, label, accent, i }) => (
                    <Link key={to} to={to} onClick={() => setIsMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.04)', textDecoration: 'none' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.48rem', color: accent, opacity: 0.6, letterSpacing: '0.2em', minWidth: '20px' }}>{String(i).padStart(2, '0')}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgb(255, 255, 255)' }}>{label}</span>
                      <span style={{ marginLeft: 'auto', width: '4px', height: '4px', borderRadius: '50%', background: accent, opacity: 0.4 }} />
                    </Link>
                  ))}
                </nav>
                <div style={{ padding: '1rem 1.5rem 1.5rem' }}>
                  {!isAuthenticated ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <Link to="/auth/login" onClick={() => setIsMenuOpen(false)} style={{ display: 'block', textAlign: 'center', padding: '0.85rem 1.5rem', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', borderRadius: '2px' }}>Connexion</Link>
                      <Link to="/auth/register" onClick={() => setIsMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '0.9rem 1.5rem', background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#020617', textDecoration: 'none', borderRadius: '2px', boxShadow: '0 4px 20px rgba(245,158,11,0.25)' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#020617', opacity: 0.4, animation: 'blink 1.4s ease-in-out infinite' }} />
                        Inscription — Essai gratuit
                      </Link>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button onClick={() => { navigate('/users/me'); setIsMenuOpen(false); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.85rem', border: '1px solid rgba(34,211,238,0.2)', background: 'rgba(34,211,238,0.05)', color: '#e06667', cursor: 'pointer', borderRadius: '2px', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}><Users size={14} /> Mon profil</button>
                      <button onClick={() => { logout(); setIsMenuOpen(false); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.85rem', border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.05)', color: 'rgba(239,68,68,0.7)', cursor: 'pointer', borderRadius: '2px', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}><LogOut size={14} /> Déconnexion</button>
                    </div>
                  )}
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.22em', color: 'rgba(255,255,255,0.15)', textTransform: 'uppercase' }}>PESACORE Architect · v1.0</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.2em', color: 'rgba(16,185,129,0.5)', textTransform: 'uppercase' }}>● STABLE</span>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export function Hero() {
  const [activeDomain, setActiveDomain] = useState(0);
  const [tempParams, setTempParams] = useState({ bienEtre: 50, equite: 50, mediation: 50 });
  const [actionLog, setActionLog] = useState([]);
  
  useEffect(() => { 
    const t = setInterval(() => setActiveDomain(d => (d + 1) % DOMAINES.length), 2200); 
    return () => clearInterval(t); 
  }, []);

  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden selection:bg-amber-500/30" style={{ background: "#020617", minHeight: "calc(100vh - 58px)", paddingTop: "58px" }}>

      {/* Fond (gardé identique) */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 30%, rgba(34,211,238,0.04) 0%, transparent 30%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 70%, rgba(245,158,11,0.04) 0%, transparent 30%)" }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full" style={{ background: "radial-gradient(circle at 50% 30%, rgba(34,211,238,0.08), transparent 50%)" }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-full" style={{ background: "radial-gradient(circle at 50% 70%, rgba(245,158,11,0.06), transparent 50%)" }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundRepeat: "repeat" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">

          {/* ── COLONNE GAUCHE : TEXTE ── */}
          <div className="flex flex-col justify-center order-2 lg:order-1">

            <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: "easeOut" }} className="flex items-center gap-3 mb-6">
              <span style={{ fontFamily: "monospace", fontSize: "0.6rem", letterSpacing: "0.36em", textTransform: "uppercase", color: "#020617", background: "linear-gradient(135deg, #f59e0b, #fbbf24)", padding: "0.3rem 0.85rem", borderRadius: "2px" }}>
                Bureau des affaires ouvertes
              </span>
              <div className="flex sm:hidden items-center" style={{ fontFamily: "monospace", fontSize: "0.56rem", letterSpacing: "0.28em", textTransform: "uppercase" }}>
                <AnimatePresence mode="wait">
                  <motion.span key={activeDomain} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0, color: "#e06667" }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}>{DOMAINES[activeDomain].label}</motion.span>
                </AnimatePresence>
              </div>
              <div className="hidden sm:flex items-center gap-2" style={{ fontFamily: "monospace", fontSize: "0.56rem", letterSpacing: "0.28em", textTransform: "uppercase" }}>
                {DOMAINES.map((d, i) => (
                  <motion.span key={i} animate={{ opacity: activeDomain === i ? 1 : 0.22, color: activeDomain === i ? "#e06667" : "#cccccc" }} transition={{ duration: 0.4 }}>
                    {d.label}{i < DOMAINES.length - 1 && <span style={{ opacity: 0.2, marginLeft: "0.4rem" }}>/</span>}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            <div className="overflow-hidden mb-3">
              <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ fontFamily: "'Georgia', serif", fontWeight: 300, lineHeight: 1.05, letterSpacing: "-0.03em" }}
                className="text-5xl md:text-6xl lg:text-7xl text-white"
              >
                Chaque situation<br />
                est une{' '}
                <em style={{ fontStyle: "italic", background: "linear-gradient(to right, #f59e0b, #fbbf24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  affaire à instruire.
                </em>
              </motion.h1>
            </div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.85 }} transition={{ delay: 0.65 }} style={{ fontFamily: "'Georgia', serif", fontSize: "1.05rem", lineHeight: 1.78, color: "#94a3b8", maxWidth: "46ch", marginBottom: "2rem" }}>
              PESACORE construit des environnements où comprendre, c'est{' '}
              <span style={{ color: "#e2e8f0" }}>traverser la situation</span> — avec rigueur.
              Chaque décision laisse une trace. Chaque bifurcation documente une transformation.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="flex items-center gap-6 mb-8" style={{ borderLeft: "2px solid rgba(245,158,11,0.3)", paddingLeft: "1rem" }}>
              {[{ n: "15+", label: "ans de terrain" }, { n: "50%", label: "retour emploi J1" }, { n: "100%", label: "secteurs couverts" }].map((s, i) => (
                <div key={i} className="flex flex-col">
                  <span style={{ fontFamily: "monospace", fontSize: "1.1rem", color: "#f59e0b", letterSpacing: "-0.03em", fontWeight: 300 }}>{s.n}</span>
                  <span style={{ fontFamily: "monospace", fontSize: "0.48rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "#cccccc" }}>{s.label}</span>
                </div>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a href="/manifeste"
                className="group relative inline-flex items-center gap-2 overflow-hidden transition-all active:scale-95"
                style={{ padding: "1rem 2.25rem", background: "linear-gradient(135deg, #f59e0b, #fbbf24, #f59e0b)", color: "#020617", textDecoration: "none", fontFamily: "monospace", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, transition: "box-shadow 0.3s" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 36px rgba(245,158,11,0.45)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 0 0 rgba(245,158,11,0)"}
              >
                <Search size={12} /> Le Manifeste
              </a>
              <a href="/contact" className="group inline-flex items-center gap-2 transition-all"
                style={{ padding: "1rem 2rem", border: "1px solid rgba(255,255,255,0.08)", color: "#64748b", textDecoration: "none", fontFamily: "monospace", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", borderRadius: "2px", transition: "border-color 0.25s, color 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(245,158,11,0.35)"; e.currentTarget.style.color = "#f59e0b"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#64748b"; }}
              >
                Essayer gratuitement <ArrowRight size={11} />
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="flex items-center gap-2 mt-5" style={{ fontFamily: "monospace", fontSize: "0.56rem", color: "#a5b3c7", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              <Check size={12} style={{ color: "#10b981" }} />
              Aucune carte bancaire requise
            </motion.div>
          </div>

          {/* ── COLONNE DROITE : TABLEAU ── */}
          <div className="flex flex-col order-1 lg:order-2">

            <motion.div 
              initial={{ opacity: 0, y: 24 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }} 
              style={{ width: '100%' }}
            >
              {/* Label */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.85rem' }}
              >
                <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(34,211,238,0.3))' }} />
                <span style={{ fontFamily: 'monospace', fontSize: '0.44rem', letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(34,211,238,0.5)' }}>Tableau d'investigation</span>
                <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(245,158,11,0.3))' }} />
              </motion.div>

              {/* Tableau */}
              <TableauEnquete />

              {/* Légende */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
                style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '1.25rem', justifyContent: 'center', flexWrap: 'wrap' }}
              >
                {[
                    { color: '#e06667', label: 'Dossier' },
                    { color: '#a78bfa', label: 'Acteur' },
                    { color: '#f59e0b', label: 'Trace · Bifurcation' },
                    { color: '#fb7185', label: 'Frein' },
                    { color: '#f59e0b', label: 'Résultat' },
                  ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)' }}>{label}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Slogan bas */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-8 pt-8 mt-8"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p style={{ fontFamily: "monospace", fontSize: "0.58rem", letterSpacing: "0.28em", textTransform: "uppercase", color: "#cccccc" }}>
            Instruire <span style={{ color: "#f59e0b" }}>/</span> Reconstituer <span style={{ color: "#e06667" }}>/</span> Transformer
          </p>
          <div className="flex items-center gap-2" style={{ fontFamily: "monospace", fontSize: "0.52rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#a5b3c7" }}>
            <span style={{ color: "#cccccc" }}>Affaires actives : 1</span>
            <span style={{ color: "#e06667" }}>·</span>
            <span style={{ color: "#f59e0b" }}>Investigation : En cours</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const RolesSection = ({ onRoleClick }) => (
  <section className="py-24 px-6 lg:px-12 bg-[#020617] relative overflow-hidden">
    <div className="absolute top-1/4 -left-24 w-[600px] h-[300px] bg-indigo-500/5 blur-[120px] pointer-events-none" />
    <div className="absolute bottom-1/4 -right-24 w-[600px] h-[300px] bg-amber-500/5 blur-[120px] pointer-events-none" />
    <div className="max-w-7xl mx-auto relative z-10">
      <div className="flex flex-col items-center mb-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-[1px] w-8 bg-gradient-to-r from-transparent via-indigo-500 to-amber-500" />
          <h3 className="text-[10px] font-mono uppercase tracking-[0.6em] text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-amber-500">Votre rôle dans l'affaire</h3>
          <span className="h-[1px] w-8 bg-gradient-to-l from-transparent via-amber-500 to-indigo-500" />
        </div>
        <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight text-center">
          Choisissez votre <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-amber-400">position</span>
        </h2>
        <p className="mt-4 text-sm font-mono text-slate-200 text-center max-w-lg">Trois entrées dans le même système. Une logique invariante, des perspectives distinctes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {ROLES.map((role, i) => {
          const nodeId = String(i + 1).padStart(2, '0');
          const AC = {
            indigo:   { border: 'border-indigo-500/10 hover:border-indigo-500/30',   glow: 'from-indigo-500/5',   icon: 'text-indigo-400 group-hover:text-indigo-300',   badge: 'text-indigo-500/50 border-indigo-500/20',   title: 'group-hover:text-indigo-400',   ey: '#e06667', dot: 'bg-indigo-500/40',   ex: 'text-indigo-500/50 group-hover:text-indigo-400',   ci: 'border-indigo-500/20 group-hover:border-indigo-500/50',   cd: 'bg-indigo-400' },
            amber:  { border: 'border-amber-500/10 hover:border-amber-500/30',  glow: 'from-amber-500/5',  icon: 'text-amber-400 group-hover:text-amber-300',  badge: 'text-amber-500/50 border-amber-500/20',  title: 'group-hover:text-amber-400',  ey: '#f59e0b', dot: 'bg-amber-500/40',  ex: 'text-amber-500/50 group-hover:text-amber-400',  ci: 'border-amber-500/20 group-hover:border-amber-500/50',  cd: 'bg-amber-400' },
            orange: { border: 'border-orange-500/10 hover:border-orange-500/30', glow: 'from-orange-500/5', icon: 'text-orange-400 group-hover:text-orange-300', badge: 'text-orange-500/50 border-orange-500/20', title: 'group-hover:text-orange-400', ey: '#f97316', dot: 'bg-orange-500/40', ex: 'text-orange-500/50 group-hover:text-orange-400', ci: 'border-orange-500/20 group-hover:border-orange-500/50', cd: 'bg-orange-400' },
          };
          const c = AC[role.accentColor];
          return (
            <motion.div key={role.id} whileHover={{ y: -8, transition: { duration: 0.3 } }} onClick={() => onRoleClick(role)}
              className={`relative group bg-slate-950/40 border rounded-2xl p-8 backdrop-blur-xl cursor-pointer transition-all duration-500 overflow-hidden ${c.border}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-b ${c.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
              <div className="flex justify-between items-start mb-5">
                <div className={`p-3 bg-white/5 rounded-xl border border-white/5 transition-all duration-500 group-hover:scale-110 ${c.icon}`}>{React.cloneElement(role.icon, { size: 28, strokeWidth: 1.5 })}</div>
                <div className={`text-[8px] font-mono tracking-tighter uppercase border px-2 py-1 rounded transition-colors ${c.badge}`}>DOSSIER {nodeId}</div>
              </div>
              <p style={{ fontFamily: 'monospace', fontSize: '0.44rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: c.ey, opacity: 0.7, marginBottom: '0.3rem' }}>{role.eyebrow}</p>
              <h4 className={`text-lg tracking-tight text-white mb-1 transition-colors ${c.title}`} style={{ fontFamily: "'Georgia', serif", fontWeight: 400, fontStyle: 'italic' }}>{role.tagline}</h4>
              <p style={{ fontFamily: 'monospace', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '0.85rem' }}>{role.name}</p>
              <p className="text-slate-400 text-sm leading-relaxed mb-8 font-light min-h-[3.5rem]">{role.description}</p>
              <div className="space-y-3 mb-8">
                {role.features.map((f, fi) => (
                  <div key={fi} className="flex items-start gap-3 group/item">
                    <div className={`mt-1.5 w-1 h-1 rounded-full transition-colors ${c.dot}`} />
                    <span className="text-xs text-slate-400 group-hover/item:text-slate-200 transition-colors font-mono tracking-wide">{f}</span>
                  </div>
                ))}
              </div>
              <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                <span className={`text-[10px] font-mono transition-colors ${c.ex}`}>OUVRIR_DOSSIER()</span>
                <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all duration-500 ${c.ci}`}>
                  <div className={`w-1 h-1 rounded-full transition-all group-hover:scale-150 ${c.cd}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const PricingSection = ({ onPlanSelect }) => {
  const [activePlan, setActivePlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null); // ← Nouvel état pour le clic

  const CC = {
    indigo:   { gradient: 'from-indigo-500/40',   glow: 'bg-indigo-500',   badge: 'bg-indigo-500',   dot: 'bg-indigo-400 shadow-[0_0_8px_rgba(34,211,238,0.4)]',   btn: 'bg-white/5 text-indigo-400 border border-white/10 hover:border-indigo-500/50 hover:bg-indigo-500/5', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    amber:  { gradient: 'from-amber-500/40',  glow: 'bg-amber-500',  badge: 'bg-amber-500',  dot: 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]',  btn: 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)]', text: 'text-amber-400', border: 'border-amber-500/20' },
    orange: { gradient: 'from-orange-500/40', glow: 'bg-orange-500', badge: 'bg-orange-500', dot: 'bg-orange-400 shadow-[0_0_8px_rgba(249,115,22,0.4)]', btn: 'bg-white/5 text-orange-400 border border-white/10 hover:border-orange-500/50 hover:bg-orange-500/5', text: 'text-orange-400', border: 'border-orange-500/20' },
  };

  // Fonction pour gérer le clic sur un plan
  const handlePlanClick = (planId) => {
    if (selectedPlan === planId) {
      setSelectedPlan(null); // Désélectionne si déjà sélectionné
    } else {
      setSelectedPlan(planId);
    }
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-[#020617]">
      <div className="max-w-7xl mx-auto">

        {/* ── En-tête ── */}
        <div className="flex flex-col items-center mb-20">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-[1px] w-12 bg-gradient-to-r from-transparent via-indigo-500 to-amber-500" />
            <h3 className="text-[10px] font-mono uppercase tracking-[0.6em] text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-amber-500">Accès-Protocole</h3>
            <div className="h-[1px] w-12 bg-gradient-to-l from-transparent via-amber-500 to-indigo-500" />
          </div>
          <h2 className="text-3xl md:text-5xl font-extralight text-white tracking-tight mb-4">
            Plans de <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-amber-400 to-indigo-400">Déploiement</span>
          </h2>
          <p className="text-sm text-slate-400 font-mono flex items-center gap-2">
            <Zap size={14} className="text-amber-500" /> Essai gratuit sans carte bancaire
          </p>
        </div>

        {/* ── Axe Particulier — bande horizontale compacte ── */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-white/5" />
            <span className="text-[9px] font-mono uppercase tracking-[0.4em] text-slate-300">Accès individuel</span>
            <div className="h-px flex-1 bg-white/5" />
          </div>

          {/* Bande — un seul bloc avec séparateurs verticaux */}
          <div className="relative p-[1px] rounded-2xl bg-gradient-to-r from-white/5 via-white/8 to-white/5">
            <div className="bg-slate-950/80 backdrop-blur-xl rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
                {PRICING_PARTICULIER.map((plan) => {
                  const c = CC[plan.accentColor];
                  const isHov = activePlan === plan.id;
                  const isSelected = selectedPlan === plan.id;
                  return (
                    <motion.div
                      key={plan.id}
                      onMouseEnter={() => setActivePlan(plan.id)}
                      onMouseLeave={() => setActivePlan(null)}
                      onClick={() => handlePlanClick(plan.id)} // ← Clic pour sélectionner
                      className={`relative px-7 py-6 flex flex-col gap-4 transition-all duration-300 cursor-pointer ${isHov ? 'bg-white/3' : ''} ${isSelected ? 'ring-2 ring-amber-500/30' : ''}`}
                    >
                      {/* Halo hover */}
                      {isHov && <div className={`absolute inset-0 ${c.glow} opacity-[0.03] blur-2xl pointer-events-none`} />}

                      {/* Indicateur de sélection */}
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <span className="text-[8px] font-mono text-amber-400 uppercase tracking-widest bg-amber-500/10 px-2 py-0.5 rounded-full">Sélectionné</span>
                        </div>
                      )}

                      {/* Nom + prix sur la même ligne */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-[9px] font-mono uppercase tracking-widest mb-0.5 ${c.text} opacity-60`}>{plan.id}</p>
                          <p className="text-base font-bold text-white uppercase tracking-tight">{plan.name}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className={`text-2xl font-light ${isHov ? 'text-white' : 'text-slate-300'} transition-colors`}>{plan.price}</span>
                            <span className="text-[10px] font-mono text-slate-300 uppercase">{plan.period}</span>
                          </div>
                          {plan.trial && (
                            <span className={`text-[8px] font-mono ${c.text} opacity-60 uppercase tracking-wider`}>
                              Essai {plan.trial}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Features — compactes */}
                      <ul className="space-y-1.5 flex-1">
                        {plan.features.map((f, fi) => (
                          <li key={fi} className="flex items-center gap-2">
                            <div className={`w-1 h-1 rounded-full flex-shrink-0 ${c.dot}`} />
                            <span className="text-[11px] text-slate-400 font-light leading-tight">{f}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlanSelect(plan.id);
                        }}
                        className={`w-full py-2.5 rounded-lg font-mono text-[10px] uppercase tracking-[0.15em] transition-all ${c.btn}`}
                      >
                        {plan.cta}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Axe Organisation — cards pleine hauteur ── */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-px flex-1 bg-white/5" />
          <span className="text-[9px] font-mono uppercase tracking-[0.4em] text-slate-300">Déploiement organisation</span>
          <div className="h-px flex-1 bg-white/5" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 relative">
          {PRICING_PLANS.map((plan) => {
            const isHoveindigo = activePlan === plan.id;
            const isSelected = selectedPlan === plan.id;
            const isDimmed = activePlan && activePlan !== plan.id && selectedPlan !== plan.id;
            const c = CC[plan.accentColor];
            return (
              <motion.div
                key={plan.id}
                onMouseEnter={() => setActivePlan(plan.id)}
                onMouseLeave={() => setActivePlan(null)}
                onClick={() => handlePlanClick(plan.id)} // ← Clic pour sélectionner
                whileHover={{ y: -5 }}
                className={`relative group p-[1px] rounded-3xl transition-all duration-500 cursor-pointer
                  ${plan.highlighted ? `bg-gradient-to-b ${c.gradient} to-transparent` : 'bg-gradient-to-b from-white/10 to-transparent'}
                  ${isDimmed ? 'opacity-40 grayscale-[0.5] scale-[0.98]' : 'opacity-100'}
                  ${isHoveindigo ? 'scale-105 z-20' : 'z-10'}
                  ${isSelected ? 'ring-2 ring-amber-500/40 scale-105 z-20' : ''}`}
              >
                <div className="bg-slate-950/90 backdrop-blur-xl rounded-[23px] p-8 h-full flex flex-col relative overflow-hidden">
                  {/* Indicateur de sélection */}
                  {isSelected && (
                    <div className="absolute top-4 left-4 z-10">
                      <span className="text-[8px] font-mono text-amber-400 uppercase tracking-widest bg-amber-500/20 px-2.5 py-0.5 rounded-full border border-amber-500/20">
                        ✓ Sélectionné
                      </span>
                    </div>
                  )}

                  <div className={`absolute -top-24 -left-24 w-48 h-48 ${c.glow} blur-[80px] rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-700`} />
                  {plan.trial && (
                    <div className="absolute top-4 right-6">
                      <span className={`text-[8px] font-black uppercase tracking-widest ${c.badge} text-white px-2 py-0.5 rounded flex items-center gap-1`}>
                        <Sparkles size={10} /> Essai {plan.trial}
                      </span>
                    </div>
                  )}
                  <div className="mb-8">
                    <h4 className="text-xs font-mono text-slate-200 uppercase tracking-widest mb-2">{plan.id}</h4>
                    <p className="text-2xl font-bold text-white uppercase tracking-tighter">{plan.name}</p>
                  </div>
                  <div className="mb-8">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-light text-white">{plan.price}</span>
                      <span className="text-xs font-mono text-slate-200 uppercase">{plan.period}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-4 leading-relaxed line-clamp-2">{plan.description}</p>
                  </div>
                  <ul className="space-y-4 mb-10 flex-grow">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3">
                        <div className={`mt-1.5 w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        <span className="text-[13px] text-slate-300 font-light leading-none">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlanSelect(plan.id);
                    }}
                    className={`w-full py-4 rounded-xl font-mono text-xs uppercase tracking-[0.2em] transition-all relative overflow-hidden group/btn ${c.btn}`}
                  >
                    <span className="relative z-10">{plan.cta}</span>
                    <div className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 bg-gradient-to-t from-white/10 to-transparent transition-transform duration-300" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Manifestes sur clic (plus hover) ── */}
        <div className="mt-20 relative">
          <AnimatePresence mode="wait">
            {selectedPlan && ['academia', 'pro', 'entreprise'].includes(selectedPlan) ? (
              <motion.div 
                key={selectedPlan} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -10 }}
                className="relative"
              >
                {/* Bouton pour fermer le manifeste */}
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="absolute -top-4 -right-4 z-20 text-slate-400 hover:text-white transition-colors bg-slate-950/80 rounded-full p-2 border border-white/10"
                >
                  <X size={16} />
                </button>

                <div className="absolute -left-4 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-indigo-500/40 via-50% via-amber-500/40 to-transparent hidden lg:block" />
                <div className={`rounded-[2.5rem] p-[1px] bg-gradient-to-br ${selectedPlan === 'academia' ? 'from-amber-500/30 to-indigo-500/20' : 'from-orange-500/30 to-amber-500/20'}`}>
                  <div className="bg-slate-950/80 backdrop-blur-3xl rounded-[2.4rem] overflow-hidden border border-white/5 shadow-[0_40px_100px_rgba(0,0,0,0.8)]">
                    {selectedPlan === 'academia' && <AcademiaManifesto />}
                    {(selectedPlan === 'pro' || selectedPlan === 'entreprise') && <EntrepriseManifesto />}
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="h-32 flex flex-col items-center justify-center border-t border-white/5">
                <div className="flex items-center gap-3 ">
                  <div className="w-1 h-1 bg-gradient-to-r from-indigo-500 to-amber-500 rounded-full animate-ping" />
                  <span className="text-[10px] font-mono text-white bg-clip-text bg-gradient-to-r from-indigo-500 to-amber-500 uppercase tracking-[0.4em]">
                    Cliquez sur un plan organisation pour voir le détail...
                  </span>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

export const AcademiaManifesto = () => (
  <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="w-full p-8 md:p-12 text-left">
    <div className="flex flex-wrap items-center gap-6 mb-12 border-b border-white/5 pb-8">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-amber-500 rounded-full" /><h2 className="text-2xl font-light tracking-tighter text-white uppercase">PESACORE<span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-amber-500">_Academia</span></h2></div>
        <p className="text-[10px] font-mono text-indigo-500/50 uppercase tracking-[0.2em]">Infrastructure de simulation causale · Pensée systémique · Analyse décisionnelle</p>
      </div>
      <div className="flex gap-4">
        <div className="px-3 py-1 border border-white/5 bg-white/5 rounded text-[9px] font-mono text-slate-400">REF: PESACORE_PEDAGOGIE_2026</div>
        <div className="px-3 py-1 border border-indigo-500/20 bg-indigo-500/5 rounded text-[9px] font-mono text-indigo-400/70">COHORTES_PILOTES · 2011–2025</div>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7 space-y-10">
        <section>
          <p className="text-xl md:text-2xl text-slate-200 leading-tight font-light tracking-tight italic border-l-2 border-amber-500/30 pl-6">"Comprendre un mécanisme en le vivant."</p>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed font-light">PESACORE construit des environnements où les logiques complexes deviennent des situations à traverser. L'apprenant n'analyse pas un système — il le vit, avec des conséquences réelles dans la simulation.</p>
        </section>
        <div className="grid grid-cols-1 gap-6">
          {[{ id: '01', title: 'Situations ancrées dans le réel', desc: 'Chaque parcours part de cas concrets, enquêtes terrain, données sectorielles. Pas de scénarios arbitraires.', color: 'amber' }, { id: '02', title: 'Mise en situation décisionnelle', desc: "Les points de bascule systémiques deviennent des choix à opérer dans la simulation. La compréhension émerge de l'action.", color: 'indigo' }, { id: '03', title: 'Traçabilité et réflexivité', desc: 'Chaque décision, hésitation, bifurcation est documentée. Le dashboard fournit une lecture précise de ce qui a été produit différemment.', color: 'amber' }].map(item => (
            <div key={item.id} className="flex gap-6 group">
              <span className={`text-xs font-mono text-${item.color}-500/40 group-hover:text-${item.color}-400 transition-colors shrink-0`}>{item.id}—</span>
              <div><h4 className="text-sm font-bold text-white uppercase mb-1">{item.title}</h4><p className="text-[13px] text-slate-400 font-light leading-snug">{item.desc}</p></div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-5 bg-white/5 rounded-2xl p-6 border border-white/5 self-start">
        <h3 className="text-[10px] font-mono text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-amber-400 uppercase tracking-widest mb-6">Ce que l'établissement observe</h3>
        <ul className="space-y-6">
          {[{ label: 'Décision incarnée', text: "Les apprenants traversent les logiques plutôt que de les étudier passivement.", color: 'amber' }, { label: 'Traces exploitables', text: "Chaque parcours génère une documentation consultable par l'équipe pédagogique.", color: 'indigo' }, { label: 'Analytics de cohorte', text: "Visualisation des écarts de compréhension et de décision au niveau de la promotion.", color: 'amber' }].map((obj, i) => (
            <li key={i} className="group/li"><span className={`text-[10px] font-bold text-slate-200 block mb-1 uppercase tracking-tighter group-hover/li:text-${obj.color}-500 transition-colors`}>[{obj.label}]</span><p className="text-xs text-slate-300 font-light">{obj.text}</p></li>
          ))}
        </ul>
        <div className="mt-10 pt-6 border-t border-white/5"><p className="text-[10px] font-mono text-slate-200 uppercase leading-relaxed">Note : PESACORE n'adapte pas la méthodologie selon l'établissement. L'établissement configure le contexte — la logique reste invariante.</p></div>
      </div>
    </div>
  </motion.div>
);

export const EntrepriseManifesto = () => (
  <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="w-full p-8 md:p-12 text-left">
    <div className="flex justify-between items-end border-b border-white/5 pb-8 mb-12">
      <div><h2 className="text-4xl font-black text-white tracking-tighter">PESACORE<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">_ENTREPRISE</span></h2><p className="text-xs font-mono text-orange-500/50 uppercase tracking-[0.3em] mt-2">Simulation décisionnelle · Formation comportementale · Mesure de transformation</p></div>
      <div className="text-right"><div className="text-[9px] font-mono text-slate-200 uppercase tracking-widest mb-1">Cohortes pilotes · 2024–2026</div><div className="flex items-center gap-3 justify-end"><span className="text-lg font-black text-orange-400">−30%</span><span className="text-[9px] text-slate-200 font-mono uppercase">turnover envisagé</span></div></div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-8">
        <section className="mb-12">
          <h3 className="text-[10px] font-mono text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 uppercase tracking-widest mb-4">Le problème qu'on résout</h3>
          <p className="text-xl text-slate-200 font-light leading-snug">La formation traditionnelle change les connaissances. Elle ne change pas les <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">comportements</span>. PESACORE construit des environnements où vos équipes prennent de vraies décisions — avec des conséquences visibles — avant que la situation réelle ne se présente.</p>
        </section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-6 bg-gradient-to-br from-orange-500/10 via-amber-500/10 to-transparent border border-orange-500/10 rounded-xl"><h4 className="text-xs font-bold text-white uppercase mb-2">Livrable</h4><p className="text-xs text-slate-400 leading-relaxed font-light">Des simulations construites à partir de vos situations réelles — cas de management, conflits documentés, contextes métier spécifiques.</p></div>
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl"><h4 className="text-xs font-bold text-white uppercase mb-2">Mesurable</h4><p className="text-xs text-slate-400 leading-relaxed font-light">Chaque simulation documente les décisions prises, les hésitations, les bifurcations. Le DRH dispose d'une preuve de transformation plutôt qu'un bilan de satisfaction.</p></div>
        </div>
      </div>
      <div className="lg:col-span-4 space-y-4">
        <h3 className="text-[10px] font-mono text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 uppercase tracking-widest mb-4">Situations traitées</h3>
        {[{ title: 'Conduite du changement', body: "Simuler l'impact d'une restructuration sur les équipes avant de l'annoncer.", color: 'orange' }, { title: 'Postures managériales', body: "Faire vivre les mécanismes de biais et d'autorité pour ancrer un changement réel.", color: 'amber' }, { title: 'Situations de crise', body: 'Négociation et arbitrage fondés sur des corpus de situations réelles documentées.', color: 'orange' }].map((uc, idx) => (
          <div key={idx} className={`p-4 border border-white/5 bg-slate-900/40 rounded-lg hover:border-${uc.color}-500/30 transition-colors cursor-default`}>
            <span className={`text-[10px] font-bold text-${uc.color}-500 block mb-1 uppercase`}>{uc.title}</span>
            <p className="text-[11px] text-slate-400 font-light">{uc.body}</p>
          </div>
        ))}
        <div className="pt-6"><p className="text-[10px] italic text-slate-200 text-center leading-relaxed">"Votre dernier plan de formation — vous savez ce qu'il a changé concrètement ?"</p></div>
      </div>
    </div>
  </motion.div>
);

export const Footer = () => (
  <footer className="border-t border-gradient-to-r from-indigo-500/20 via-amber-500/20 to-transparent py-12 px-8 mt-16 bg-slate-950/50">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
      <div>
        <div className="flex items-center gap-3 mb-4"><img src="/logo2026.png" alt="PESACORE" className="h-7 w-7" /><span className="text-xl font-mono uppercase text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-amber-400">PESACORE</span></div>
        <p className="text-sm text-slate-400 font-mono">© 2026 PESACORE Architect. Tous droits réservés.</p>
      </div>
      <div className="flex gap-8">
        <Link to="/manifeste" className="text-sm text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-indigo-400 hover:to-amber-400 transition-all font-mono">À propos</Link>
        <Link to="/contact" className="text-sm text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-indigo-400 hover:to-amber-400 transition-all font-mono">Contact</Link>
        <Link to="/cgu" className="text-sm text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-indigo-400 hover:to-amber-400 transition-all font-mono">CGV/CGU</Link>
        <Link to="/confidentialite" className="text-sm text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-indigo-400 hover:to-amber-400 transition-all font-mono">Politique de Confidentialité</Link>
        <a href="https://pesacore.fr" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-indigo-400 hover:to-amber-400 transition-all font-mono">Support</a>
      </div>
    </div>
  </footer>
);


const HomePageLab = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleRoleClick = (role) => {
    if (isAuthenticated) {
      // Déjà connecté → dashboard ou admin
      navigate(role.id === 'learner' ? '/dashboard' : '/admin');
      return;
    }

    // Primo arrivant
    const isRegistrationOpen = process.env.REACT_APP_REGISTRATION_OPEN === 'true';
    const contactEmail = process.env.REACT_APP_ADMIN_EMAIL || 'admin@pesacore.fr';

    if (!isRegistrationOpen) {
      navigate('/auth/register-blocked', {
        state: {
          role: role.id,                    // 'learner' ou 'admin' (superviseur)
          email: contactEmail,
        },
      });
      return;
    }

    // Inscriptions ouvertes
    navigate('/auth/register', { state: { role: role.id } });
  };

  const handlePlanSelect = (planId) => {
    // Entreprise → toujours vers /contact
    if (planId === 'entreprise') {
      navigate('/contact');
      return;
    }

    // Contrôle des inscriptions via .env
    const isRegistrationOpen = process.env.REACT_APP_REGISTRATION_OPEN === 'true';
    const contactEmail = process.env.REACT_APP_ADMIN_EMAIL || 'admin@pesacore.fr';

    // Si les inscriptions sont fermées pour academia et pro
    if (!isRegistrationOpen && (planId === 'academia' || planId === 'pro')) {
      navigate('/auth/register-blocked', {
        state: {
          plan: planId,
          email: contactEmail,
        },
      });
      return;
    }

    // Inscriptions ouvertes → comportement normal
    navigate('/auth/register', { state: { plan: planId } });
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 relative overflow-x-hidden font-sans selection:bg-amber-500/30">
      <div className="fixed inset-0 pointer-events-none z-0">
        <PerspectiveGrid />
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/10 via-[#020617] via-50% to-amber-950/10" />
        <FloatingWords />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.05)_50%)] bg-[length:100%_4px] opacity-20" />
      </div>

      <Header />

      <main className="relative z-10">
        <Hero />
        <div className="h-24 bg-gradient-to-b from-transparent via-[#020617] to-[#020617]" />
        <RolesSection onRoleClick={handleRoleClick} />
        <PricingSection onPlanSelect={handlePlanSelect} />

        <section className="py-32 px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/5 to-amber-950/5" />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-4xl md:text-6xl font-extralight tracking-tighter text-white mb-10">
                Prêt à découvrir<br />
                <span className="font-bold italic font-serif bg-gradient-to-r from-indigo-400 via-amber-400 to-indigo-400 bg-clip-text text-transparent">Pesacore Architect ?</span>
              </h2>
              <button
                onClick={() => {
                  const isRegistrationOpen = process.env.REACT_APP_REGISTRATION_OPEN === 'true';
                  const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || 'admin@pesacore.fr';

                  if (!isRegistrationOpen) {
                    navigate('/auth/register-blocked', {
                      state: { email: contactEmail }
                    });
                  } else {
                    navigate('/auth/register');
                  }
                }}
                className="group relative inline-flex items-center gap-4 px-12 py-5 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-950 font-bold uppercase tracking-[0.2em] text-sm rounded-full overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] active:scale-95"
              >
                <span className="relative z-10">Démarrer</span>
                <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-indigo-300 to-indigo-400 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </button>
              
            </motion.div>
          </div>
        </section>        
      </main>

      <Footer />
    </div>
  );
};

export default HomePageLab;